import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, money, fileUrl } from "../../lib/api";
import { useApp } from "../../context/AppContext";
import { Page, btnGold, btnGhost } from "../../components/store/ui";

export default function CartPage() {
  const { cart, setCart, locale, t, user } = useApp();
  const nav = useNavigate();
  const setQty = async (vid, qty) => {
    try {
      const { data } = await api.patch(`/cart/items/${vid}`, { qty }, { params: { market: locale.market } });
      setCart(data);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const blocked = cart.items.some((i) => !i.available);
  return (
    <Page title={t("cart")}>
      {!cart.items.length ? (
        <div data-testid="cart-empty"><p className="text-muted-foreground">{t("empty_cart")}</p><Link to="/shop" className={`${btnGhost} inline-block mt-6`}>{t("continue")}</Link></div>
      ) : (
        <div className="grid lg:grid-cols-[1fr_360px] gap-12">
          <div className="divide-y divide-white/5" data-testid="cart-lines">
            {cart.items.map((i) => (
              <div key={i.variant_id} className="py-6 flex gap-5" data-testid={`cart-line-${i.variant_id}`}>
                <img src={fileUrl(i.image, 300)} alt="" loading="lazy" decoding="async" className="w-24 h-28 object-cover" />
                <div className="flex-1">
                  <Link to={`/product/${i.product_id}`} className="text-sm hover:text-[#C9A96E]">{i.title}</Link>
                  <div className="label-xs mt-1">{i.variant_name}</div>
                  {!i.available && <div className="text-xs text-red-400 mt-2" data-testid="cart-line-unavailable">{i.reason}</div>}
                  <div className="flex items-center gap-2 mt-4">
                    <button data-testid={`cart-minus-${i.variant_id}`} onClick={() => setQty(i.variant_id, i.qty - 1)} className="border border-white/10 p-1.5"><Minus className="w-3 h-3" /></button>
                    <span className="font-mono w-6 text-center">{i.qty}</span>
                    <button data-testid={`cart-plus-${i.variant_id}`} onClick={() => setQty(i.variant_id, i.qty + 1)} className="border border-white/10 p-1.5"><Plus className="w-3 h-3" /></button>
                    <button data-testid={`cart-remove-${i.variant_id}`} onClick={() => setQty(i.variant_id, 0)} className="ml-4 text-muted-foreground hover:text-white"><X className="w-4 h-4" /></button>
                  </div>
                </div>
                <div className="font-mono text-sm">{money(i.line_total, cart.currency, locale.lang)}</div>
              </div>
            ))}
          </div>
          <aside className="bg-[#14161D] border border-white/10 p-8 h-fit space-y-3 text-sm" data-testid="cart-summary">
            <div className="flex justify-between"><span>{t("subtotal")}</span><span className="font-mono">{money(cart.subtotal, cart.currency, locale.lang)}</span></div>
            <div className="flex justify-between text-muted-foreground"><span>Livraison</span><span className="font-mono">incluse</span></div>
            {(cart.taxes || []).map((x) => <div key={x.name} className="flex justify-between text-muted-foreground"><span>{x.name} ({(x.rate * 100).toFixed(3).replace(/\.?0+$/, "")} %)</span><span className="font-mono">{money(x.amount, cart.currency, locale.lang)}</span></div>)}
            <div className="flex justify-between border-t border-white/10 pt-3 text-base"><span>{t("total")}</span><span className="font-mono" data-testid="cart-total">{money(cart.total, cart.currency, locale.lang)}</span></div>
            <div className="text-xs text-muted-foreground">{cart.vat_included ? t("vat_incl") : "Taxes estimées pour le Québec (ajustées selon votre province au paiement)"}</div>
            <button data-testid="cart-checkout-button" disabled={blocked} onClick={() => nav("/checkout")} className={`${btnGold} w-full mt-4`}>{t("checkout")}</button>
            {blocked && <div className="text-xs text-red-400">Retirez les articles indisponibles pour continuer.</div>}
          </aside>
        </div>
      )}
    </Page>
  );
}
